import { type TableSelectedCell, useTableRows } from '@/components/custom/erp';
import type { ReceiptCellValue } from '../types';

export type SelectedCellInfo = TableSelectedCell;

type DetailRowBase = {
  uuid: string;
  line?: number;
  ma_loai_hd?: string;
  [key: string]: unknown;
};

const ensureRowDefaults = <T extends DetailRowBase>(row: T): T => ({
  ...row,
  ma_loai_hd: row.ma_loai_hd ?? '0'
});

const normalizeInitialRows = <T extends DetailRowBase>(rows: T[] = []): T[] => {
  if (rows && rows.length > 0) {
    return rows.map((row, index) =>
      ensureRowDefaults({
        ...row,
        uuid: row.uuid && row.uuid !== '' ? row.uuid : `temp-${Date.now()}-${index}`
      } as T)
    );
  }

  return [
    ensureRowDefaults({
      uuid: `temp-${Date.now()}`,
      line: 1
    } as T)
  ];
};

export function useDetailRows<T extends DetailRowBase>(
  initialRows: T[] = [],
  onChange?: (rows: T[]) => void,
  onFieldValueChange?: (row: T, field: string, value: ReceiptCellValue) => Partial<T>
) {
  const normalizedRows = normalizeInitialRows(initialRows);

  const {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow: baseHandleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<T>({
    initialRows: normalizedRows,
    onChange,
    onFieldValueChange: (row, field, value) =>
      ({
        ...(onFieldValueChange?.(row, field, value) ?? {})
      }) as Partial<T>,
    defaultField: 'dien_giai'
  });

  const handleAddRow = () => {
    baseHandleAddRow();
    setRows(prev => {
      if (!prev.length) return prev;
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      updated[lastIndex] = ensureRowDefaults(updated[lastIndex]);
      return updated;
    });
  };

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  };
}
